import React, { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import {
  CheckCircle, Circle, AlertTriangle, Clock, ArrowRight, Loader2, Bell, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const SEVERITY_COLORS = {
  high: "border-red-200 bg-red-50/60 text-red-700",
  medium: "border-amber-200 bg-amber-50/60 text-amber-700",
  low: "border-blue-200 bg-blue-50/60 text-blue-700",
};

export function ContractHealthPanel({ contractId, onNavigateTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await apiClient.get(`/contracts/${contractId}/health-check`);
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [contractId]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (loading) return null;
  if (!data) return null;

  const { checklist, checklist_progress, gaps, nudges } = data;
  const allDone = checklist_progress.percentage === 100 && gaps.length === 0 && nudges.length === 0;

  if (allDone && collapsed) return null;

  return (
    <div className="border rounded-sm overflow-hidden" data-testid="contract-health-panel">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 hover:bg-slate-50 transition-colors"
        onClick={() => setCollapsed(!collapsed)}
        data-testid="health-panel-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
            checklist_progress.percentage === 100 ? "bg-emerald-100 text-emerald-700" :
            checklist_progress.percentage >= 50 ? "bg-amber-100 text-amber-700" :
            "bg-slate-100 text-slate-600"
          }`}>
            {checklist_progress.percentage}%
          </div>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Contract Health
          </span>
          {gaps.length > 0 && (
            <Badge className="rounded-sm text-[10px] bg-amber-100 text-amber-700 border-amber-200">
              {gaps.length} issue{gaps.length > 1 ? "s" : ""}
            </Badge>
          )}
          {nudges.length > 0 && (
            <Badge className="rounded-sm text-[10px] bg-blue-100 text-blue-700 border-blue-200">
              {nudges.length} reminder{nudges.length > 1 ? "s" : ""}
            </Badge>
          )}
          {allDone && (
            <Badge className="rounded-sm text-[10px] bg-emerald-100 text-emerald-700">All good</Badge>
          )}
        </div>
        {collapsed ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronUp size={14} className="text-slate-400" />}
      </button>

      {!collapsed && (
        <div className="px-4 py-3 space-y-3 border-t">
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  checklist_progress.percentage === 100 ? "bg-emerald-500" :
                  checklist_progress.percentage >= 50 ? "bg-amber-500" : "bg-slate-400"
                }`}
                style={{ width: `${checklist_progress.percentage}%` }}
              />
            </div>
            <span className="text-[11px] text-slate-400 tabular-nums">{checklist_progress.completed}/{checklist_progress.total}</span>
          </div>

          {/* Checklist */}
          <div className="space-y-1" data-testid="health-checklist">
            {checklist.map((item) => (
              <button
                key={item.id}
                className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-sm text-left transition-colors ${
                  item.done ? "opacity-60" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                }`}
                onClick={() => !item.done && onNavigateTab(item.action)}
                disabled={item.done}
                data-testid={`checklist-${item.id}`}
              >
                {item.done
                  ? <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                  : <Circle size={16} className="text-slate-300 mt-0.5 shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.done ? "line-through text-slate-400" : "text-slate-700 dark:text-slate-300"}`}>
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{item.description}</p>
                </div>
                {!item.done && <ArrowRight size={12} className="text-slate-300 mt-1 shrink-0" />}
              </button>
            ))}
          </div>

          {/* Gaps */}
          {gaps.length > 0 && (
            <div className="space-y-1.5" data-testid="health-gaps">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Issues Detected</p>
              {gaps.map((gap) => (
                <button
                  key={gap.id}
                  className={`w-full flex items-start gap-2.5 px-3 py-2 rounded-sm border text-left transition-colors hover:opacity-80 ${SEVERITY_COLORS[gap.severity]}`}
                  onClick={() => onNavigateTab(gap.action)}
                  data-testid={`gap-${gap.id}`}
                >
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{gap.title}</p>
                    <p className="text-[11px] opacity-80 mt-0.5">{gap.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Nudges */}
          {nudges.length > 0 && (
            <div className="space-y-1.5" data-testid="health-nudges">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Reminders</p>
              {nudges.map((nudge) => (
                <button
                  key={nudge.id}
                  className="w-full flex items-start gap-2.5 px-3 py-2 rounded-sm border border-blue-100 bg-blue-50/30 text-left transition-colors hover:bg-blue-50/60"
                  onClick={() => onNavigateTab(nudge.action)}
                  data-testid={`nudge-${nudge.id}`}
                >
                  {nudge.type === "reminder" ? <Clock size={14} className="text-blue-500 mt-0.5 shrink-0" /> : <Bell size={14} className="text-blue-500 mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-700">{nudge.title}</p>
                    <p className="text-[11px] text-blue-500 mt-0.5">{nudge.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
